import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type MouseEvent, type ReactNode, type RefObject } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import {
    CaretDownIcon,
    ArrowsOutSimpleIcon,
    CalendarCheckIcon,
    ChatCircleTextIcon,
    CheckCircleIcon,
    LifebuoyIcon,
    MapPinIcon,
    PauseIcon,
    PhoneIcon,
    PlayCircleIcon,
    PlayIcon,
    SparkleIcon,
    SpeakerHighIcon,
    SpeakerSlashIcon,
    UsersThreeIcon,
    XIcon,
    type Icon,
} from "@phosphor-icons/react";
import { Moon, Sun } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useTheme } from "../components/ThemeContext";
import { QuiltLogo } from "../components/QuiltLogo";
import { DownloadQr, StoreButton } from "../components/StoreDownload";
import welwynnLogo from "../assets/welwynn/welwynn-logo.png";
import welwynnLogoDark from "../assets/welwynn/welwynn-logo-dark.png";
import step1Img from "../assets/getstarted/step1.png";
import type { Clip } from "../config/providerVideos";
import { submitN8nForm } from "../services/n8nService";
import PhoneMockup from "../components/PhoneMockup";
import QuiltSwipeApp from "../components/QuiltSwipeApp";
import { WelwynnJoinDialog } from "../components/WelwynnJoinDialog";
import { clinicIdFor } from "../services/joinRequestService";

/**
 * Unlisted landing page for Welwynn patients (quilt.gg/welwynn): noindex, not in the nav or a sitemap.
 * Follows the site's light/dark theme and the patient page's look.
 */
const CONTENT = {
    privacyPolicy: "https://web.medical.quilt.gg/legal/privacy",
    // From welwynn.com (contact page: welwynn.com/contact-us). They don't publish an email address or office hours.
    clinic: {
        website: "https://welwynn.com",
        phone: "(984) 200-2780",
        phoneHref: "tel:+19842002780",
        contactForm: "https://welwynn.com/contact-us/",
        address: "7200 Falls of Neuse Rd #300, Raleigh, NC 27615",
        mapHref: "https://maps.app.goo.gl/ZeGFP9zoBXuVSoa36",
    },
    crisisLine: "988",
};

// Accents from the Quilt logo. Magenta and teal only reach AA for large text and icons; small links use purple.
const MAGENTA = "#D7348F";
const TEAL = "#2BA2A8";
const PURPLE = "#7A51AF";

const focusRing =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500";
const muted = "text-slate-600 dark:text-slate-400";
const link = `rounded font-medium text-purple-600 underline underline-offset-4 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 ${focusRing}`;
// Same card treatment as the patient page
const card = "rounded-3xl border border-slate-200 bg-white/80 shadow-sm backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-950/70";

/** In-page link that glides to its target (instantly when the user prefers reduced motion). */
function ScrollLink({ to, children }: { to: string; children: ReactNode }) {
    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
        const target = document.getElementById(to);
        if (!target) return;
        e.preventDefault();
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        history.replaceState(null, "", `#${to}`);
    };
    return (
        <a href={`#${to}`} onClick={onClick} className={link}>
            {children}
        </a>
    );
}

/** Welwynn's logo: dark lettering on a light pill in light mode, the original white lettering in dark mode. */
function WelwynnLogo({ className = "" }: { className?: string }) {
    return (
        <>
            <img src={welwynnLogoDark} alt="Welwynn" width={120} height={28} className={`w-auto dark:hidden ${className}`} />
            <img src={welwynnLogo} alt="Welwynn" width={120} height={28} className={`hidden w-auto dark:block ${className}`} />
        </>
    );
}

/* ------------------------------------------------------------------ */
/* Small building blocks                                                */
/* ------------------------------------------------------------------ */

/** Fades content up the first time it scrolls into view. Off when the user prefers reduced motion. */
function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!("IntersectionObserver" in window)) {
            setShown(true);
            return;
        }
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    io.disconnect();
                }
            },
            { rootMargin: "0px 0px -8% 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
            className={`transition-[opacity,transform] duration-700 ease-out motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none ${shown ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
                } ${className}`}
        >
            {children}
        </div>
    );
}

/** Eyebrow + light heading, as on the patient page. */
function SectionHeading({ eyebrow, title, id }: { eyebrow: string; title: string; id: string }) {
    return (
        <div className="mb-8 text-center md:mb-12">
            <p className="mb-3 text-sm font-medium uppercase tracking-widest text-purple-500">{eyebrow}</p>
            <h2 id={id} className="text-3xl font-light leading-tight text-slate-900 md:text-5xl dark:text-white">
                {title}
            </h2>
        </div>
    );
}

/** Rounded icon tile, using Phosphor's duotone weight. */
function IconTile({ icon: IconCmp, color }: { icon: Icon; color: string }) {
    return (
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}1f`, color }}>
            <IconCmp weight="duotone" size={26} aria-hidden="true" />
        </span>
    );
}

/** Store buttons everywhere; on desktop, a QR code underneath so people can scan with their phone. */
function GetTheApp({ align = "start" }: { align?: "start" | "center" }) {
    return (
        <div className={`flex flex-col gap-6 ${align === "center" ? "items-center" : "items-stretch lg:items-start"}`}>
            <div className="flex w-full max-w-sm gap-3 sm:w-auto sm:max-w-none">
                <StoreButton store="apple" className="flex-1 sm:flex-none" />
                <StoreButton store="google" className="flex-1 sm:flex-none" />
            </div>
            {/* Desktop only: scan to open the right store on your phone */}
            <div className="hidden items-center gap-5 lg:flex">
                <DownloadQr size="lg" caption="" />
                <p className={`max-w-[11rem] text-sm leading-relaxed ${muted}`}>Or scan with your phone's camera to get the app.</p>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Header                                                               */
/* ------------------------------------------------------------------ */

/** Same glass bar as the rest of the site, but with no navigation: Quilt and Welwynn in the middle, the theme switch on the right. */
function WelwynnHeader() {
    const { theme, toggleTheme } = useTheme();
    const dark = theme === "dark";

    return (
        <header
            className={`fixed left-0 right-0 top-0 z-50 border-b backdrop-blur-lg md:m-2 md:rounded-full md:border ${dark
                ? "border-cyan-50/15 bg-gradient-to-b from-zinc-950/80 to-fuchsia-950/20 md:border-fuchsia-300/20"
                : "border-cyan-300/20 bg-gradient-to-b from-white/85 to-white/40 md:border-slate-200/70"
                }`}
        >
            {/* Quilt, "brought to you by", Welwynn, centred; the theme switch sits on the right */}
            <div className="mx-auto grid h-16 max-w-[1440px] grid-cols-[2.75rem_1fr_2.75rem] items-center gap-2 px-3 sm:px-4">
                <span aria-hidden="true" />
                <div className="flex min-w-0 items-center justify-center gap-2.5 sm:gap-4">
                    <Link to="/" onClick={() => window.scrollTo(0, 0)} className={`flex shrink-0 items-center gap-1.5 rounded-xl ${focusRing}`} aria-label="Quilt home">
                        <QuiltLogo alt="" className="h-11 w-11 sm:h-12 sm:w-12" />
                        <span className="hidden text-xl text-slate-900 sm:inline dark:text-white">Quilt</span>
                    </Link>
                    <span className="whitespace-nowrap text-[11px] text-slate-500 sm:text-sm dark:text-slate-400">brought to you by</span>
                    <WelwynnLogo className="h-4 shrink-0 sm:h-6" />
                </div>
                <button
                    type="button"
                    onClick={toggleTheme}
                    aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
                    className={`inline-flex size-11 items-center justify-center rounded-full transition-colors ${dark ? "text-slate-300 hover:bg-white/10 hover:text-white" : "text-slate-600 hover:bg-slate-900/5 hover:text-slate-900"
                        } ${focusRing}`}
                >
                    {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
            </div>
        </header>
    );
}

/* ------------------------------------------------------------------ */
/* Step 1: ways to ask the clinic                                       */
/* ------------------------------------------------------------------ */

/** Every field the support workflow expects, filled in for a Welwynn sign-up request (only the email comes from the patient). */
function welwynnSignUpRequest(email: string) {
    return {
        type: "support" as const,
        source: "welwynn_signup_request",
        page: "/welwynn",
        user_role: "patient",
        name: "New Welwynn patient",
        email,
        phone: "0",
        clinic_name: "Welwynn",
        organization_name: "Welwynn",
        address: "-",
        zip_code: "0",
        topic: "Welwynn new patient sign-up",
        message: `New Welwynn patient asking to join Quilt (from quilt.gg/welwynn). Please register ${email} under Welwynn so they get an invite code by email.`,
    };
}

/** Asks Welwynn to sign the patient up, through the same support form workflow as the rest of the site. */
function SignUpRequestForm() {
    const [email, setEmail] = useState("");
    const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
    const [error, setError] = useState("");
    const id = useId();

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setState("sending");
        // Hidden field that people never fill in; bots usually do
        if (new FormData(e.currentTarget).get("website")) return setState("done");
        const result = await submitN8nForm(welwynnSignUpRequest(email.trim().toLowerCase()));
        if (result.success) {
            setState("done");
        } else {
            setError(result.error || "Something went wrong. Please try again.");
            setState("error");
        }
    };

    if (state === "done") {
        return (
            <div role="status" className="flex items-start gap-3 rounded-2xl bg-white p-4 ring-1 ring-slate-200 dark:bg-zinc-900 dark:ring-zinc-700">
                <CheckCircleIcon size={22} weight="duotone" className="mt-0.5 shrink-0 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    <strong className="font-medium text-slate-900 dark:text-white">Request sent.</strong> Welwynn will sign you up, then look out
                    for your invite code by email.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit}>
            <label htmlFor={`${id}-email`} className="mb-2 block text-sm font-medium text-slate-900 dark:text-white">
                Request a sign-up
            </label>
            <div className="flex items-center gap-1.5 rounded-full bg-white p-1.5 ring-1 ring-slate-300 focus-within:ring-2 focus-within:ring-purple-500 dark:bg-zinc-900 dark:ring-zinc-700">
                <input
                    id={`${id}-email`}
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={`${id}-note`}
                    className="min-w-0 flex-1 bg-transparent pl-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white dark:placeholder:text-slate-500"
                />
                <button
                    type="submit"
                    disabled={state === "sending"}
                    className={`min-h-10 shrink-0 rounded-full bg-purple-600 px-4 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-60 dark:bg-purple-500 dark:hover:bg-purple-400 ${focusRing}`}
                >
                    {state === "sending" ? "Sending…" : "Send"}
                </button>
            </div>
            {/* Honeypot: hidden from people and screen readers */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-px w-px opacity-0" />
            {state === "error" && (
                <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            <p id={`${id}-note`} className={`mt-2 text-xs leading-relaxed ${muted}`}>
                We'll pass your email to Welwynn so they can sign you up.{" "}
                <a href={CONTENT.privacyPolicy} target="_blank" rel="noopener noreferrer" className={link}>
                    Privacy policy
                </a>
            </p>
        </form>
    );
}

/** Sits where step 2 has its picture, so the row stays even. */
function AskClinicPanel({ onJoin }: { onJoin: () => void }) {
    const { phone, phoneHref } = CONTENT.clinic;

    return (
        <div className="relative -mx-2 -mb-2 flex aspect-[600/510] flex-col justify-center gap-3 rounded-2xl bg-purple-50/70 p-5 md:-mx-3 md:-mb-3 dark:bg-white/[0.04]">
            <button
                type="button"
                onClick={onJoin}
                className={`flex min-h-12 w-full items-center justify-center rounded-full bg-purple-600 px-5 text-[15px] font-medium text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-400 ${focusRing}`}
            >
                Request to join
            </button>
            <div className={`flex items-center gap-3 text-xs uppercase tracking-widest ${muted}`} aria-hidden="true">
                <span className="h-px flex-1 bg-slate-300 dark:bg-zinc-700" />
                or
                <span className="h-px flex-1 bg-slate-300 dark:bg-zinc-700" />
            </div>
            <a
                href={phoneHref}
                className={`flex min-h-12 w-full items-center justify-center gap-2.5 rounded-full border border-slate-300 bg-white px-5 text-[15px] font-medium text-slate-900 transition-colors hover:bg-slate-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800 ${focusRing}`}
            >
                <PhoneIcon size={20} weight="duotone" aria-hidden="true" />
                Call {phone}
            </a>
            <p className={`flex items-center justify-center gap-2 text-sm ${muted}`}>
                <CalendarCheckIcon size={18} weight="duotone" aria-hidden="true" />
                Or just ask at your next visit
            </p>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* FAQ accordion                                                        */
/* ------------------------------------------------------------------ */

function FaqItem({ question, children }: { question: string; children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const id = useId().replace(/:/g, "");
    const panelRef = useRef<HTMLDivElement>(null);

    // inert keeps a closed panel's links out of the tab order and away from screen readers
    useEffect(() => {
        if (panelRef.current) (panelRef.current as HTMLDivElement & { inert: boolean }).inert = !open;
    }, [open]);

    return (
        <div className="border-b border-slate-200 last:border-b-0 dark:border-zinc-800">
            <h3>
                <button
                    type="button"
                    id={`${id}-q`}
                    aria-expanded={open}
                    aria-controls={`${id}-a`}
                    onClick={() => setOpen((o) => !o)}
                    className={`flex w-full items-center justify-between gap-4 rounded-xl px-1 py-5 text-left text-base font-medium text-slate-800 md:text-lg dark:text-slate-100 ${focusRing}`}
                >
                    {question}
                    <CaretDownIcon
                        weight="bold"
                        size={20}
                        aria-hidden="true"
                        className={`shrink-0 text-purple-500 transition-transform duration-300 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
                    />
                </button>
            </h3>
            {/* grid-rows 0fr <-> 1fr animates to and from the panel's natural height */}
            <div
                ref={panelRef}
                id={`${id}-a`}
                role="region"
                aria-labelledby={`${id}-q`}
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            >
                <div className="min-h-0 overflow-hidden">
                    <div className={`px-1 pb-5 leading-relaxed ${muted}`}>{children}</div>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Help cards: clip on the left, text on the right; tap to watch big    */
/* ------------------------------------------------------------------ */

const prefersLessData = () =>
    typeof navigator !== "undefined" && Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);

type HelpItem = { title: string; text: string; clips: string[] };

/** Instagram-story style segments: watched clips are full, the playing one fills with the video. */
function StoryBars({ count, index, videoRef, className = "" }: { count: number; index: number; videoRef: RefObject<HTMLVideoElement | null>; className?: string }) {
    const fillRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        let frame = 0;
        const tick = () => {
            const video = videoRef.current;
            const fill = fillRef.current;
            if (video && fill && video.duration) fill.style.transform = `scaleX(${Math.min(1, video.currentTime / video.duration)})`;
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [index, videoRef]);

    return (
        <div aria-hidden="true" className={`pointer-events-none flex gap-1 drop-shadow-[0_1px_1px_rgba(0,0,0,0.45)] ${className}`}>
            {Array.from({ length: count }, (_, i) => (
                <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/35">
                    <span
                        ref={i === index ? fillRef : undefined}
                        className="block h-full origin-left rounded-full bg-white"
                        style={{ transform: `scaleX(${i < index ? 1 : 0})` }}
                    />
                </span>
            ))}
        </div>
    );
}

/** Muted preview that only downloads and plays while it's on screen. */
function ClipPreview({ src, loop, onEnded, videoRef }: { src: string; loop: boolean; onEnded: () => void; videoRef: RefObject<HTMLVideoElement | null> }) {
    const [load, setLoad] = useState(false);
    const visible = useRef(false);
    const reduce = useRef(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || prefersLessData() || typeof IntersectionObserver === "undefined") return;
        reduce.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const io = new IntersectionObserver(
            ([entry]) => {
                visible.current = entry.isIntersecting;
                if (entry.isIntersecting) {
                    setLoad(true);
                    if (!reduce.current && video.currentSrc) video.play().catch(() => {});
                } else {
                    video.pause();
                }
            },
            { threshold: 0.4 }
        );
        io.observe(video);
        return () => io.disconnect();
    }, [videoRef]);

    // Starts the first clip once it's allowed to load, and carries on to the next one in a sequence
    useEffect(() => {
        const video = videoRef.current;
        if (video && load && visible.current && !reduce.current) video.play().catch(() => {});
    }, [src, load, videoRef]);

    return (
        <video
            ref={videoRef}
            src={load ? `${src}#t=0.1` : undefined}
            className="h-full w-full object-cover"
            muted
            loop={loop}
            playsInline
            preload={load ? "auto" : "none"}
            onEnded={onEnded}
            aria-hidden="true"
        />
    );
}

function HelpCard({ item, open, onOpen }: { item: HelpItem; open: boolean; onOpen: (opener: HTMLElement, clip: number) => void }) {
    const [clip, setClip] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const many = item.clips.length > 1;

    return (
        // ~0.375rem around the clip, with the inner corner radius following the card's
        <div className={`flex h-full items-stretch gap-4 p-1.5 ${card}`}>
            <button
                type="button"
                onClick={(e) => onOpen(e.currentTarget, clip)}
                aria-label={many ? `Watch ${item.clips.length} sample videos: ${item.title}` : `Watch a sample video: ${item.title}`}
                className={`group relative aspect-[9/16] w-24 shrink-0 self-start sm:w-28 ${focusRing}`}
            >
                {!open && (
                    <motion.div layoutId={`clip-${item.title}`} className="absolute inset-0 overflow-hidden rounded-[18px] bg-gradient-to-br from-purple-200 to-cyan-100 dark:from-purple-900/60 dark:to-cyan-900/40">
                        <ClipPreview src={item.clips[clip]} loop={!many} onEnded={() => setClip((c) => (c + 1) % item.clips.length)} videoRef={videoRef} />
                        {many && <StoryBars count={item.clips.length} index={clip} videoRef={videoRef} className="absolute inset-x-2 top-2" />}
                        <span className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-transform duration-200 group-hover:scale-110">
                            <ArrowsOutSimpleIcon weight="bold" size={14} aria-hidden="true" />
                        </span>
                    </motion.div>
                )}
            </button>
            <div className="flex flex-col justify-center py-3 pr-3">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">{item.title}</h3>
                <p className={`mt-1 font-light leading-relaxed ${muted}`}>{item.text}</p>
            </div>
        </div>
    );
}

/** The clip grows out of its card into the middle of the screen; everything else dims. Plays like a story. */
function ClipLightbox({ item, startClip, onClose }: { item: HelpItem; startClip: number; onClose: () => void }) {
    const closeRef = useRef<HTMLButtonElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [clip, setClip] = useState(startClip);
    const [soundOff, setSoundOff] = useState(false);
    const [paused, setPaused] = useState(false);
    const count = item.clips.length;
    const many = count > 1;

    const next = useCallback(() => setClip((c) => (c + 1) % count), [count]);
    const prev = useCallback(() => {
        const video = videoRef.current;
        // Like a story: going back early in a clip goes to the previous one, later on restarts it
        if (video && (video.currentTime > 2 || !many)) video.currentTime = 0;
        else setClip((c) => (c - 1 + count) % count);
    }, [count, many]);
    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) video.play().catch(() => {});
        else video.pause();
    };

    useEffect(() => {
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowRight" && many) next();
            else if (e.key === "ArrowLeft") prev();
        };
        document.addEventListener("keydown", onKey);
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = overflow;
        };
    }, [onClose, next, prev, many]);

    // Plays each clip with sound; if the browser won't allow sound, plays muted and the sound button turns it on
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.play().catch(() => {
            video.muted = true;
            setSoundOff(true);
            video.play().catch(() => {});
        });
    }, [clip]);

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = soundOff;
    }, [soundOff]);

    const control = `flex size-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition-colors hover:bg-black/60 ${focusRing}`;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={item.title}>
            <motion.div
                className="absolute inset-0 bg-black/75 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={onClose}
            />
            <div className="relative flex flex-col items-center gap-3">
                <motion.div
                    layoutId={`clip-${item.title}`}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                    className="relative aspect-[9/16] h-[min(82svh,calc((100vw-2rem)*16/9))] overflow-hidden rounded-3xl bg-black shadow-2xl"
                >
                    <video
                        ref={videoRef}
                        src={item.clips[clip]}
                        className="h-full w-full object-cover"
                        playsInline
                        loop={!many}
                        onEnded={next}
                        onPlay={() => setPaused(false)}
                        onPause={() => setPaused(true)}
                    />
                    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/45 to-transparent" />
                    <StoryBars count={count} index={clip} videoRef={videoRef} className="absolute inset-x-3 top-3" />

                    {/* Tap the sides to move between clips and the middle to pause, as in a story */}
                    <div className="absolute inset-x-0 bottom-16 top-8 flex">
                        <button type="button" onClick={prev} aria-label={many ? "Previous video" : "Restart video"} className="w-1/3 focus-visible:outline-none" />
                        <button type="button" onClick={togglePlay} aria-label={paused ? "Play" : "Pause"} className="flex-1 focus-visible:outline-none" />
                        <button type="button" onClick={many ? next : togglePlay} aria-label={many ? "Next video" : paused ? "Play" : "Pause"} className="w-1/3 focus-visible:outline-none" tabIndex={many ? 0 : -1} />
                    </div>

                    <div className="absolute bottom-3 right-3 flex gap-2">
                        <button type="button" onClick={togglePlay} aria-label={paused ? "Play" : "Pause"} className={control}>
                            {paused ? <PlayIcon weight="fill" size={18} aria-hidden="true" /> : <PauseIcon weight="fill" size={18} aria-hidden="true" />}
                        </button>
                        <button type="button" onClick={() => setSoundOff((m) => !m)} aria-label={soundOff ? "Turn sound on" : "Turn sound off"} className={control}>
                            {soundOff ? <SpeakerSlashIcon weight="fill" size={18} aria-hidden="true" /> : <SpeakerHighIcon weight="fill" size={18} aria-hidden="true" />}
                        </button>
                    </div>
                    {paused && (
                        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md">
                            <PlayIcon weight="fill" size={28} />
                        </span>
                    )}
                </motion.div>
                <motion.p
                    className="text-center text-sm font-medium text-white/90"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    {item.title}
                    {many && <span className="text-white/60"> · {clip + 1} of {count}</span>}
                </motion.p>
            </div>
            <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label="Close video"
                className={`absolute right-3 top-3 flex size-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25 ${focusRing}`}
            >
                <XIcon weight="bold" size={20} aria-hidden="true" />
            </button>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

// Welwynn's own clips live in public/welwynn/clips; cards without one yet use a Quilt clip that fits the theme.
// Add more files to a card's list and it plays them in turn, story style.
const welwynnClip = (name: string) => `/welwynn/clips/${name}.mp4`;
const HELP_ITEMS: HelpItem[] = [
    { title: "Support your recovery", text: "Stay on track between visits.", clips: [welwynnClip("recovery-1")] },
    { title: "Manage cravings", text: "Short videos to help you get through the urge.", clips: [welwynnClip("cravings-1"), welwynnClip("cravings-2")] },
    { title: "Build better habits", text: "Small daily steps that add up over time.", clips: [welwynnClip("habits-1"), welwynnClip("habits-2")] },
    { title: "Navigate triggers", text: "Learn to spot them and plan a healthier response.", clips: [welwynnClip("triggers-1")] },
    { title: "Build resilience", text: "Grow the strength to bounce back from setbacks.", clips: [welwynnClip("resilience-1"), welwynnClip("resilience-2")] },
];
// The phone demo at the top deals from the same clips as the help cards
const HELP_CLIPS: Clip[] = HELP_ITEMS.flatMap((item) => item.clips.map((src, i) => ({ src, category: item.title, title: `${item.title} ${i + 1}` })));


// color for light mode, colorDark (lighter tint) for dark mode
const DIFFERENT_ITEMS: { icon: Icon; color: string; colorDark: string; title: string; text: string }[] = [
    { icon: PlayCircleIcon, color: MAGENTA, colorDark: "#F07DBE", title: "Your feed", text: "Quilt curates a daily feed tailored to your well-being." },
    { icon: SparkleIcon, color: TEAL, colorDark: "#5FD3D8", title: "Your algorithm", text: "Your feed learns what works best for you with every swipe." },
    { icon: UsersThreeIcon, color: PURPLE, colorDark: "#B79BE0", title: "Your care team", text: "Your provider adjusts your feed as you progress." },
];

export default function WelwynnPage() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const [openClip, setOpenClip] = useState<{ title: string; clip: number } | null>(null);
    // Join form: opens by itself from the sign-up links Quilt emails (?clinic=<id>) and the marketing email (?join)
    const [joinOpen, setJoinOpen] = useState(() => {
        const q = new URLSearchParams(window.location.search);
        return q.has("clinic") || q.has("join");
    });
    const [clinicId] = useState(() => clinicIdFor(window.location.search, import.meta.env.VITE_WELWYNN_CLINIC_ID));
    const openItem = HELP_ITEMS.find((i) => i.title === openClip?.title);
    // The clip that opened the lightbox gets focus back when it closes (Safari doesn't focus buttons on click)
    const openerRef = useRef<HTMLElement | null>(null);
    const closeClip = useCallback(() => {
        setOpenClip(null);
        openerRef.current?.focus({ preventScroll: true });
    }, []);
    // The italic serif accent is only used here, so it's only loaded here
    useEffect(() => {
        const font = document.createElement("link");
        font.rel = "stylesheet";
        font.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@1&display=swap";
        document.head.appendChild(font);

        // No analytics on this page: index.html skips loading it here, and this also stops it if it was already
        // loaded (e.g. arriving from another page of the site)
        const gaOff = "ga-disable-G-5T3DE1KX4C";
        (window as unknown as Record<string, unknown>)[gaOff] = true;
        return () => {
            font.remove();
            delete (window as unknown as Record<string, unknown>)[gaOff];
        };
    }, []);

    return (
        <div className="relative z-10 min-h-screen overflow-x-clip text-slate-900 dark:text-white">
            <SEO
                title="Quilt for Welwynn patients"
                description="Quilt is an app from Welwynn. Short daily videos, tailored to you, with your care team by your side between visits."
                path="/welwynn"
                noindex
            />
            {/* Soft, static wash in the logo colours (the site's interactive dot grid is off on this page) */}
            <div aria-hidden="true" className="wel-bg pointer-events-none fixed inset-0 -z-10" />
            <style>{`
                .wel-bg {
                    background:
                        radial-gradient(55rem 38rem at 8% -8%, rgba(122, 81, 175, 0.10), transparent 65%),
                        radial-gradient(48rem 34rem at 105% 35%, rgba(43, 162, 168, 0.08), transparent 65%),
                        radial-gradient(50rem 36rem at -5% 105%, rgba(215, 52, 143, 0.06), transparent 65%);
                }
                .dark .wel-bg {
                    background:
                        radial-gradient(55rem 38rem at 8% -8%, rgba(122, 81, 175, 0.16), transparent 65%),
                        radial-gradient(48rem 34rem at 105% 35%, rgba(43, 162, 168, 0.09), transparent 65%),
                        radial-gradient(50rem 36rem at -5% 105%, rgba(215, 52, 143, 0.08), transparent 65%);
                }
                .wel-serif { font-family: 'Instrument Serif', Georgia, serif; font-style: italic; font-weight: 400; }
            `}</style>

            <a
                href="#main"
                className={`fixed left-4 top-3 z-[60] -translate-y-20 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 shadow focus:translate-y-0 ${focusRing}`}
            >
                Skip to content
            </a>

            {/* 1. Header */}
            <WelwynnHeader />

            <main id="main" className="scroll-mt-24">
                {/* 2. Hero */}
                <section aria-labelledby="hero-title" className="mx-auto max-w-6xl px-4 pb-14 pt-[5.5rem] sm:px-6 md:pb-24 md:pt-32">
                    <div className="grid items-center gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
                        <div className="text-center lg:text-left">
                            <h1
                                id="hero-title"
                                className="text-balance text-[clamp(1.75rem,7.6vw,2.4rem)] font-light leading-[1.1] text-slate-900 md:text-5xl lg:text-6xl dark:text-white"
                            >
                                {/* Kept as two phrases so desktop doesn't break it one word per line */}
                                <span className="lg:whitespace-nowrap">Personalized recovery,</span>{" "}
                                <span className="lg:whitespace-nowrap">shaped daily</span>
                                <span className="wel-serif mt-1 block text-[1.15em] leading-none text-[#D7348F] dark:text-[#EC6BB4]">
                                    just for you.
                                </span>
                            </h1>
                            <p className={`mx-auto mt-3 max-w-xl text-[15px] font-light leading-relaxed md:mt-6 md:text-lg lg:mx-0 ${muted}`}>
                                Quilt is your daily feed for wellness, managed by your provider. Swipe through short daily videos, shaped around
                                your goals, to build healthier habits between visits.
                            </p>
                            <div className="mt-8 hidden lg:block">
                                <GetTheApp />
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-5">
                            {/* The same swipeable feed demo as the patient page */}
                            <PhoneMockup className="z-10">
                                <QuiltSwipeApp
                                    clips={HELP_CLIPS}
                                    customCtaText="Get started"
                                    onCustomCtaClick={() => document.getElementById("getting-started")?.scrollIntoView({ behavior: "smooth" })}
                                />
                            </PhoneMockup>
                            <div className="flex w-full justify-center lg:hidden">
                                <GetTheApp align="center" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. How Quilt can help you */}
                <section aria-labelledby="help-title" className="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
                    <Reveal>
                        <SectionHeading eyebrow="Your support" id="help-title" title="How Quilt can help you" />
                    </Reveal>
                    <MotionConfig reducedMotion="user">
                        <ul className="flex flex-wrap justify-center gap-4 md:gap-5">
                            {HELP_ITEMS.map((item, i) => (
                                <li key={item.title} className="w-full md:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.85rem)]">
                                    <Reveal delay={i * 70} className="h-full">
                                        <HelpCard item={item} open={openClip?.title === item.title} onOpen={(opener, clip) => {
                                                openerRef.current = opener;
                                                setOpenClip({ title: item.title, clip });
                                            }} />
                                    </Reveal>
                                </li>
                            ))}
                        </ul>
                        <AnimatePresence>{openItem && openClip && <ClipLightbox key={openItem.title} item={openItem} startClip={openClip.clip} onClose={closeClip} />}</AnimatePresence>
                    </MotionConfig>
                </section>

                {/* 4. What makes Quilt different */}
                <section aria-labelledby="different-title" className="mx-auto max-w-6xl px-4 py-6 sm:px-6 md:py-10">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl border border-purple-200/70 bg-gradient-to-br from-white via-purple-50/70 to-white px-6 py-12 text-slate-900 shadow-sm md:px-12 md:py-16 dark:border-zinc-800 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-950 dark:text-white">
                            <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-15 blur-3xl dark:opacity-30" style={{ background: MAGENTA }} />
                            <div aria-hidden="true" className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full opacity-15 blur-3xl dark:opacity-25" style={{ background: TEAL }} />
                            <p className="relative mb-3 text-center text-sm font-medium uppercase tracking-widest text-purple-500 dark:text-purple-300">Why Quilt</p>
                            <h2 id="different-title" className="relative mb-10 text-center text-3xl font-light leading-tight md:mb-12 md:text-5xl">
                                Why Quilt?
                            </h2>
                            <ul className="relative grid gap-8 md:grid-cols-3 md:gap-10">
                                {DIFFERENT_ITEMS.map((item) => (
                                    <li key={item.title} className="flex items-start gap-4 md:flex-col md:items-center md:text-center">
                                        <span
                                            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl dark:bg-white/10"
                                            style={{ color: dark ? item.colorDark : item.color, backgroundColor: dark ? undefined : `${item.color}1a` }}
                                        >
                                            <item.icon weight="duotone" size={26} aria-hidden="true" />
                                        </span>
                                        <div>
                                            <h3 className="text-lg font-medium">{item.title}</h3>
                                            <p className="mt-1 font-light leading-relaxed text-slate-600 dark:text-[#C9C3D6]">{item.text}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </section>

                {/* 5. Getting started */}
                <section id="getting-started" aria-labelledby="start-title" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-14 sm:px-6 md:py-20">
                    <Reveal>
                        <SectionHeading eyebrow="Getting started" id="start-title" title="Getting started is easy" />
                    </Reveal>
                    <ol className="grid gap-4 md:grid-cols-3 md:gap-5">
                        {[
                            { title: "Request to join", text: "Fill in a short form and Welwynn will approve you on Quilt.", image: null, imageAlt: "" },
                            { title: "Look out for your invite code", text: "Quilt will send you an invite code by email.", image: step1Img, imageAlt: "An invite email with your Quilt code" },
                            { title: "Download Quilt", text: "Enter your email and invite code to activate your feed.", image: null, imageAlt: "" },
                        ].map((step, i) => (
                            <li key={step.title}>
                                <Reveal delay={i * 90} className="h-full">
                                    <div className={`flex h-full flex-col p-6 md:p-7 ${card}`}>
                                        <span
                                            className="mb-4 flex h-11 w-11 items-center justify-center rounded-full text-lg font-semibold text-white"
                                            style={{ backgroundColor: MAGENTA }}
                                            aria-hidden="true"
                                        >
                                            {i + 1}
                                        </span>
                                        <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                                            <span className="sr-only">Step {i + 1}: </span>
                                            {step.title}
                                        </h3>
                                        <p className={`mt-1 font-light leading-relaxed ${muted}`}>{step.text}</p>
                                        {/* Graphic or actions, pinned to the bottom so the three cards line up */}
                                        <div className="mt-auto pt-5">
                                            {i === 0 && <AskClinicPanel onJoin={() => setJoinOpen(true)} />}
                                            {step.image && (
                                                // Same artwork as the patient page's "How do I access Quilt?" steps
                                                <div className="relative -mx-2 -mb-2 overflow-hidden rounded-2xl md:-mx-3 md:-mb-3">
                                                    {/* Shown whole at its own 600x510 ratio, so nothing is cropped */}
                                                    <img src={step.image} alt={step.imageAlt} loading="lazy" width={600} height={510} className="aspect-[600/510] h-auto w-full object-contain" />
                                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white to-transparent dark:from-zinc-950" />
                                                </div>
                                            )}
                                            {i === 2 && (
                                                <div>
                                                    <div className="flex flex-col gap-3">
                                                        <StoreButton store="apple" />
                                                        <StoreButton store="google" />
                                                    </div>
                                                    <div className="mt-5 hidden justify-center lg:flex">
                                                        <DownloadQr />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Reveal>
                            </li>
                        ))}
                    </ol>
                    <Reveal>
                        <p className={`mt-8 text-center font-light ${muted}`}>
                            No code? Code not working?{" "}
                            <ScrollLink to="contact">Just ask your clinic.</ScrollLink>
                        </p>
                    </Reveal>
                </section>

                {/* 6. FAQ */}
                <section aria-labelledby="faq-title" className="mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-20">
                    <Reveal>
                        <SectionHeading eyebrow="FAQ" id="faq-title" title="Questions" />
                    </Reveal>
                    <Reveal>
                        <div className={`px-5 md:px-8 ${card}`}>
                            <FaqItem question="How do I start?">
                                Request to join on this page, or ask your clinician at Welwynn to sign you up. Once Welwynn approves you,
                                you'll receive an invite code by email. Then
                                download Quilt, enter your email and invite code, and your daily feed is ready.
                            </FaqItem>
                            <FaqItem question="Does Quilt replace my sessions at Welwynn?">
                                No. Quilt supports you in between your appointments. Keep going to your sessions as planned.
                            </FaqItem>
                            <FaqItem question="Is Quilt free?">
                                Quilt is offered to you through Welwynn, as part of the care they provide.
                            </FaqItem>
                            <FaqItem question="Who can see my activity on Quilt?">
                                Your providers at Welwynn can see your survey, assessment, and check-in responses, so they can support
                                you better. Read more in our{" "}
                                <a href={CONTENT.privacyPolicy} target="_blank" rel="noopener noreferrer" className={link}>
                                    privacy policy
                                </a>
                                .
                            </FaqItem>
                            <FaqItem question="How much time does it take each day?">
                                About 5 to 10 minutes. Quilt is designed to respect your time and your wellbeing.
                            </FaqItem>
                            <FaqItem question="Which phones does it work on?">
                                Quilt works on iPhone and Android phones. Download it from the App Store or Google Play.
                            </FaqItem>
                            <FaqItem question="My invite code isn't working. What do I do?">
                                Contact Welwynn and they can help you get set up.{" "}
                                <ScrollLink to="contact">See how to reach them</ScrollLink>
                                .
                            </FaqItem>
                        </div>
                    </Reveal>
                </section>

                {/* 7. Contact Welwynn */}
                <section id="contact" aria-labelledby="contact-title" className="mx-auto max-w-3xl scroll-mt-24 px-4 pb-6 sm:px-6">
                    <Reveal>
                        <div className={`p-6 md:p-10 ${card}`}>
                            <h2 id="contact-title" className="text-3xl font-light text-slate-900 md:text-4xl dark:text-white">
                                Contact Welwynn
                            </h2>
                            <p className={`mt-2 font-light ${muted}`}>For your invite code, or any questions about your care.</p>
                            <dl className="mt-6 grid gap-5 sm:grid-cols-3">
                                {[
                                    { icon: PhoneIcon, label: "Phone", value: CONTENT.clinic.phone, href: CONTENT.clinic.phoneHref, external: false },
                                    { icon: ChatCircleTextIcon, label: "Online", value: "Send them a message", href: CONTENT.clinic.contactForm, external: true },
                                    { icon: MapPinIcon, label: "Address", value: CONTENT.clinic.address, href: CONTENT.clinic.mapHref, external: true },
                                ].map(({ icon, label, value, href, external }) => (
                                    <div key={label} className="flex items-start gap-3">
                                        <IconTile icon={icon} color={PURPLE} />
                                        <div className="min-w-0">
                                            <dt className={`text-sm ${muted}`}>{label}</dt>
                                            <dd className="mt-0.5 break-words font-medium">
                                                <a href={href} className={link} {...(external && { target: "_blank", rel: "noopener noreferrer" })}>
                                                    {value}
                                                </a>
                                            </dd>
                                        </div>
                                    </div>
                                ))}
                            </dl>
                            {/* Sign-off strip: who this is, and where to read more */}
                            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6 dark:border-zinc-800">
                                <WelwynnLogo className="h-6 md:h-7" />
                                <a href={CONTENT.clinic.website} target="_blank" rel="noopener noreferrer" className={`text-sm ${link}`}>
                                    Visit welwynn.com
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* 8. Safety note */}
                <section aria-label="Safety" className="mx-auto max-w-3xl px-4 pb-14 pt-4 sm:px-6 md:pb-20">
                    <div className="flex items-start gap-3 rounded-2xl border border-pink-500/30 bg-pink-500/5 p-4 text-sm leading-relaxed text-slate-800 md:p-5 dark:border-pink-400/30 dark:bg-pink-500/10 dark:text-slate-200">
                        <LifebuoyIcon weight="duotone" size={22} className="mt-0.5 shrink-0 text-[#D7348F] dark:text-[#EC6BB4]" aria-hidden="true" />
                        <p>
                            <strong className="font-semibold">Quilt isn't for emergencies.</strong> If you're in crisis, call{" "}
                            <a href={`tel:${CONTENT.crisisLine}`} className={link}>
                                {CONTENT.crisisLine}
                            </a>{" "}
                            or contact Welwynn right away.
                        </p>
                    </div>
                </section>
            </main>

            {/* Join form, and the button that keeps it in reach anywhere on the page */}
            <WelwynnJoinDialog open={joinOpen} onOpenChange={setJoinOpen} clinicId={clinicId} fallback={<SignUpRequestForm />} />
            {!joinOpen && (
                <button
                    type="button"
                    onClick={() => setJoinOpen(true)}
                    className={`fixed bottom-4 right-4 z-40 min-h-12 rounded-full bg-purple-600 px-6 text-[15px] font-medium text-white shadow-lg shadow-purple-900/20 transition-colors hover:bg-purple-700 md:bottom-6 md:right-6 dark:bg-purple-500 dark:hover:bg-purple-400 ${focusRing}`}
                >
                    Join Quilt
                </button>
            )}

            {/* 9. Footer */}
            <footer className="border-t border-slate-200 bg-slate-50/80 backdrop-blur-sm dark:border-slate-900 dark:bg-black/80">
                <div className={`mx-auto flex max-w-6xl flex-col items-center gap-3 px-4 py-8 text-sm sm:flex-row sm:justify-between sm:px-6 ${muted}`}>
                    <div className="flex items-center gap-2.5">
                        <QuiltLogo variant="solid" alt="Quilt" className="h-7 w-7" />
                        <a href="https://quilt.gg" className={`rounded font-medium text-slate-900 dark:text-white ${focusRing}`}>
                            quilt.gg
                        </a>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-center">
                        <a
                            href={CONTENT.privacyPolicy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex min-h-11 items-center rounded underline-offset-4 hover:underline ${focusRing}`}
                        >
                            Privacy Policy
                        </a>
                        <span>© 2026 Quilt Technologies. All rights reserved.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
